/**
 * Gets the repositories of the user from Github
 */

import { call, delay, put, takeLatest } from "redux-saga/effects";
import { FILE_API_URL } from "utils/constants";
import { Res, request } from "utils/request";
import { getAssetCreateTypeFromMime } from "app/components/MediaUploader/helper";
import {
  AssetCreateTypeEnum,
  CreateAssetResponse,
  ICreatePayload,
  PreSignedUrlResponse,
} from "app/components/MediaUploader/types";
import { actions } from "app/components/MediaUploader/redux/slice";
import { handleApiError } from "utils/errorHandler";

export function* uploadMediaRequest(action: {
  payload: {
    media: ICreatePayload;
    callback: (data: CreateAssetResponse) => void;
  };
}) {
  try {
    const media = action.payload.media;
    const fileBlob = media.file;
    const mimeType =
      media.mimeType || fileBlob.type || "application/octet-stream";
    const assetType: AssetCreateTypeEnum = getAssetCreateTypeFromMime(
      mimeType,
      fileBlob.name,
    );

    const { data, err }: Res<PreSignedUrlResponse> = yield getPreSignedUrlApi({
      fileName: media.name,
    });
    if (!data || err) {
      throw err || "Something went wrong";
    }
    yield call(uploadFile, data?.signedUrl, fileBlob, data.contentType);
    const asset: CreateAssetResponse = yield createAsset({
      type: assetType,
      mimeType,
      fileName: media.name,
      filePath: data.fileNameWithPrefix,
      fileSize: fileBlob.size,
    });
    yield put(actions.uploadMediaSuccess(asset?.url));
    yield call(action.payload.callback, asset);
  } catch (err: any) {
    yield put(actions.uploadMediaError());
    handleApiError(err);
  }
}

export function* getPreSignedUrlApi({ fileName }: { fileName: string }) {
  try {
    yield delay(500);
    const options = {
      method: "POST",
      body: JSON.stringify({
        fileName,
      }),
    };
    const url = `${FILE_API_URL}asset/signed-url`;
    const { data, err }: Res<PreSignedUrlResponse> = yield call(
      request,
      url,
      options
    );
    if (err || !data) {
      throw err;
    }
    return data;
  } catch (err: any) {
    yield put(actions.uploadMediaError());
    handleApiError(err);
  }
}

export function* createAsset({
  type,
  mimeType,
  fileName,
  filePath,
  fileSize,
}: {
  type: AssetCreateTypeEnum;
  mimeType: string;
  fileName: string;
  filePath: string;
  fileSize: number;
}) {
  try {
    yield delay(500);
    const options = {
      method: "POST",
      body: JSON.stringify({
        type,
        mimeType,
        fileName,
        filePath,
        fileSize,
      }),
    };
    const url = `${FILE_API_URL}asset/create`;
    const { data, err }: Res<{ data: CreateAssetResponse }> = yield call(
      request,
      url,
      options
    );
    if (err || !data) {
      throw err;
    }
    return data?.data;
  } catch (err: any) {
    handleApiError(err);
  }
}

async function uploadFile(
  s3SignedUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", s3SignedUrl, true);
    xhr.setRequestHeader(
      "Content-Type",
      contentType || "application/octet-stream"
    );
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(
          new Error(
            `Upload failed with status ${xhr.status}: ${xhr.statusText} | Response: ${xhr.responseText}`
          )
        );
      }
    };
    xhr.onerror = () =>
      reject(new Error("Network error during upload (possible CORS issue)"));
    xhr.ontimeout = () => reject(new Error("Upload timed out"));
    xhr.send(file);
  });
}




/**
 * Root saga manages watcher lifecycle
 */
export function* mediaUploadSaga() {
  yield takeLatest(actions.uploadMedia, uploadMediaRequest);
}
